/**
 * Main-thread client for the bot AI Web Worker.
 * Lazily creates the worker, sends compute requests with Promise semantics,
 * and falls back to synchronous computation if the worker fails.
 */

import type { PlayerState, GameState, BotConfig, BotFogContext } from '../types';
import type { BotFireResult, BotJumpResult } from './bot';
import type { BotWorkerRequest, BotWorkerResponse, BotTurnPlan } from './botWorker';
import { isWasmEnabled } from './wasmPhysics';

const TIMEOUT_MS = 30_000;

export class BotWorkerClient {
  /** `timeoutMs`: how long a worker request may take before the main thread computes it instead (R8). */
  constructor(private readonly timeoutMs: number = TIMEOUT_MS) {}
  private worker: Worker | null = null;
  private nextId = 0;
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  private pending = new Map<number, {
    resolve: (value: any) => void;
    reject: (reason: unknown) => void;
    timer: ReturnType<typeof setTimeout>;
  }>();
  private failed = false; // if worker creation failed, skip retries

  protected getWorker(): Worker | null {
    if (this.failed) return null;
    if (this.worker) return this.worker;

    try {
      this.worker = new Worker(
        new URL('./botWorker.ts', import.meta.url),
        { type: 'module' }
      );

      this.worker.onmessage = (e: MessageEvent<BotWorkerResponse>) => {
        const resp = e.data;
        const entry = this.pending.get(resp.id);
        if (entry) {
          clearTimeout(entry.timer);
          this.pending.delete(resp.id);
          if (resp.type === 'fireResult') {
            entry.resolve({ result: resp.result, rngState: resp.rngState });
          } else if (resp.type === 'turnResult') {
            entry.resolve(resp.turnResult);
          } else if (resp.type === 'jumpResult') {
            entry.resolve({ result: resp.result, rngState: resp.rngState });
          }
        }
      };

      this.worker.onerror = (err) => {
        console.warn('Bot worker error:', err);
        // Reject all pending requests so they can fall back
        for (const [id, entry] of this.pending) {
          clearTimeout(entry.timer);
          entry.reject(new Error('Worker error'));
          this.pending.delete(id);
        }
      };

      return this.worker;
    } catch (err) {
      console.warn('Failed to create bot worker, using synchronous fallback:', err);
      this.failed = true;
      return null;
    }
  }

  /**
   * Compute just the fire solution (used for post-move/post-jump fire).
   */
  /**
   * R8: on a worker timeout or error, the same planner on the main thread — never a yielded turn. Headless
   * never times out, so yielding made a slow or throttled browser play a different game.
   */
  async computeBotFireAsync(
    player: PlayerState,
    gameState: GameState,
    config: BotConfig,
    rngState: number,
    fogContext?: BotFogContext
  ): Promise<{ result: BotFireResult | null; rngState: number }> {
    try {
      return await this.computeBotFireOnce(player, gameState, config, rngState, fogContext);
    } catch {
      return this.computeBotFireOnce(player, gameState, config, rngState, fogContext, false);
    }
  }

  private async computeBotFireOnce(
    player: PlayerState,
    gameState: GameState,
    config: BotConfig,
    rngState: number,
    fogContext?: BotFogContext,
    useWorker = true,
  ): Promise<{ result: BotFireResult | null; rngState: number }> {
    const worker = useWorker ? this.getWorker() : null;

    if (!worker) {
      // Synchronous fallback
      const { computeBotFire } = await import('./bot');
      const { SeededRandom } = await import('../core/random');
      const rng = SeededRandom.fromState(rngState);
      const result = computeBotFire(player, gameState, config, rng, fogContext);
      return { result, rngState: rng.getState() };
    }

    const id = this.nextId++;

    return new Promise((resolve, reject) => {
      const timer = setTimeout(() => {
        this.pending.delete(id);
        reject(new Error('Bot worker timeout'));
      }, this.timeoutMs);

      this.pending.set(id, { resolve, reject, timer });

      const request: BotWorkerRequest = {
        id,
        wasmEnabled: isWasmEnabled(),
        type: 'computeFire',
        player,
        gameState,
        config,
        rngState,
        fogContext,
      };

      worker.postMessage(request);
    });
  }

  /**
   * Plan the whole turn (transit graph, situation, plan, pre-computed
   * fire/jump/move) off the main thread. Same code path for every mode.
   */
  /**
   * R8: on a worker timeout or error, the same planner on the main thread — never a yielded turn. Headless
   * never times out, so yielding made a slow or throttled browser play a different game.
   */
  async computeBotTurnAsync(
    player: PlayerState,
    gameState: GameState,
    config: BotConfig,
    rngState: number,
    fogContext?: BotFogContext
  ): Promise<BotTurnPlan> {
    try {
      return await this.computeBotTurnOnce(player, gameState, config, rngState, fogContext);
    } catch {
      return this.computeBotTurnOnce(player, gameState, config, rngState, fogContext, false);
    }
  }

  private async computeBotTurnOnce(
    player: PlayerState,
    gameState: GameState,
    config: BotConfig,
    rngState: number,
    fogContext?: BotFogContext,
    useWorker = true,
  ): Promise<BotTurnPlan> {
    const worker = useWorker ? this.getWorker() : null;

    if (!worker) {
      // Synchronous fallback: the same planner the worker runs, JS scorer
      const { computeTurnPlan, transitFuelBonus } = await import('./botPlanner');
      const { buildTransitGraph } = await import('./transitGraph');
      const { SeededRandom } = await import('../core/random');
      const rng = SeededRandom.fromState(rngState);
      const fuelBonus = transitFuelBonus(player, gameState.config.modeConfig);
      const transitGraph = buildTransitGraph(gameState.planets, player.movementRemaining, fuelBonus, player.planetIndex);
      return computeTurnPlan(player, gameState, config, rng, fogContext, transitGraph);
    }

    const id = this.nextId++;

    return new Promise((resolve, reject) => {
      const timer = setTimeout(() => {
        this.pending.delete(id);
        reject(new Error('Bot worker timeout'));
      }, this.timeoutMs);

      this.pending.set(id, { resolve, reject, timer });

      const request: BotWorkerRequest = {
        id,
        wasmEnabled: isWasmEnabled(),
        type: 'computeTurn',
        player,
        gameState,
        config,
        rngState,
        fogContext,
      };

      worker.postMessage(request);
    });
  }

  /**
   * Compute just the jump solution (transit graph + jump search) off main thread.
   * Used by post-fire jump decision to avoid blocking rendering.
   */
  /**
   * R8: on a worker timeout or error, the same planner on the main thread — never a yielded turn. Headless
   * never times out, so yielding made a slow or throttled browser play a different game.
   */
  async computeBotJumpAsync(
    player: PlayerState,
    gameState: GameState,
    config: BotConfig,
    rngState: number,
    fogContext?: BotFogContext
  ): Promise<{ result: BotJumpResult | null; rngState: number }> {
    try {
      return await this.computeBotJumpOnce(player, gameState, config, rngState, fogContext);
    } catch {
      return this.computeBotJumpOnce(player, gameState, config, rngState, fogContext, false);
    }
  }

  private async computeBotJumpOnce(
    player: PlayerState,
    gameState: GameState,
    config: BotConfig,
    rngState: number,
    fogContext?: BotFogContext,
    useWorker = true,
  ): Promise<{ result: BotJumpResult | null; rngState: number }> {
    const worker = useWorker ? this.getWorker() : null;

    if (!worker) {
      // Synchronous fallback
      const { computeBotJump } = await import('./bot');
      const { buildTransitGraph } = await import('./transitGraph');
      const { SeededRandom } = await import('../core/random');
      const { transitFuelBonus } = await import('./botPlanner');
      const rng = SeededRandom.fromState(rngState);
      const fuelBonus = transitFuelBonus(player, gameState.config.modeConfig);   // R9: one fuel rule everywhere
      const transitGraph = buildTransitGraph(gameState.planets, player.movementRemaining, fuelBonus, player.planetIndex);
      const result = computeBotJump(player, gameState, config, rng, transitGraph, undefined, fogContext);
      return { result, rngState: rng.getState() };
    }

    const id = this.nextId++;

    return new Promise((resolve, reject) => {
      const timer = setTimeout(() => {
        this.pending.delete(id);
        reject(new Error('Bot worker timeout'));
      }, this.timeoutMs);

      this.pending.set(id, { resolve, reject, timer });

      const request: BotWorkerRequest = {
        id,
        wasmEnabled: isWasmEnabled(),
        type: 'computeJump',
        player,
        gameState,
        config,
        rngState,
        fogContext,
      };

      worker.postMessage(request);
    });
  }

  /** Eagerly create the worker so WASM begins loading immediately. */
  warmup(): void {
    this.getWorker();
  }

  terminate(): void {
    if (this.worker) {
      this.worker.terminate();
      this.worker = null;
    }
    for (const [, entry] of this.pending) {
      clearTimeout(entry.timer);
    }
    this.pending.clear();
  }
}
